import { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { Stack, SxProps, Typography, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
  TooltipProps,
} from 'recharts';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import theme from '@elementary/common-ui/style/theme';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import {
  COVERAGE_TABLE_FILTERS,
  DAYS_BACK,
} from '@elementary/common-ui/constants/urlParams';
import { COVERAGE_STATS } from '@elementary/common-ui/constants/queryKeys';
import { fetchCoverageStats } from '../../services/api';
import BarStack from './BarStack';
import { TableAssetsQueryRequest } from './types';

const SummaryWrapper = styled(Flex)`
  gap: 24px;
`;

const StatCard = styled(FlexColumn)`
  height: 148px;
  min-width: 260px;
  width: 260px;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 12px 12px 0 0;
  cursor: default;
`;

const CardHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const CardTitle = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const CardContent = styled(Stack)`
  padding: 12px 24px 12px 24px;
  gap: 8px;
`;

const ScoreContainer = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const ScoreValue = styled(Typography)`
  font-weight: 500;
  font-size: 32px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const TotalAssets = styled(Flex)`
  align-items: center;
  gap: 4px;
`;

const TotalAssetsValue = styled(Typography)`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const TotalAssetsLabel = styled(Typography)`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const DimensionsCard = styled(StatCard)`
  width: 696px;
  gap: 12px;
`;

const DimensionsContent = styled(Flex)`
  padding: 0 24px;
  height: 90px;
  align-items: center;
`;

const ChartContainer = styled(Stack)`
  width: 100%;
  height: 100%;
`;

const CustomTooltip = styled(Stack)`
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TooltipLabel = styled(Typography)`
  font-weight: 500;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const TooltipValue = styled(Typography)`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const ErrorText = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.red2};
`;

const LoadingCoverage = () => (
  <>
    <ScoreContainer>
      <Skeleton variant="text" width={50} height={48} />
      <Skeleton
        variant="rectangular"
        width={25}
        height={22}
        sx={{ borderRadius: '4px' }}
      />
    </ScoreContainer>
    <TotalAssets>
      <Skeleton variant="text" width={100} height={16} />
    </TotalAssets>
  </>
);

const ErrorCoverage = () => <ErrorText>Error loading data</ErrorText>;

const CustomTooltipContent = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <CustomTooltip>
        <TooltipLabel>{label}</TooltipLabel>
        <TooltipValue>{`${payload[0]?.value?.toFixed(
          1,
        )}% coverage`}</TooltipValue>
      </CustomTooltip>
    );
  }
  return null;
};

type CustomXAxisTickProps = {
  x: number;
  y: number;
  payload: { value: string };
  width: number;
  visibleTicksCount: number;
  chartWidth: number;
};

const CustomXAxisTick = ({
  x,
  y,
  payload,
  width,
  visibleTicksCount,
  chartWidth,
}: CustomXAxisTickProps) => {
  // Calculate the actual width for each tick based on chart width and visible ticks count
  const calculatedWidth =
    chartWidth && visibleTicksCount
      ? (chartWidth - 50) / visibleTicksCount // Subtract margins/padding
      : width;

  return (
    <foreignObject
      x={x - calculatedWidth / 2}
      y={y - 2}
      height={16}
      width={calculatedWidth}
    >
      <OverflowTooltip
        sx={{
          justifyContent: 'center',
        }}
        typographySx={{
          color: theme.palette.common.gray2,
          fontSize: '11px !important',
        }}
      >
        {payload.value}
      </OverflowTooltip>
    </foreignObject>
  );
};

const CoverageSummary = ({ sx }: { sx?: SxProps }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number | undefined>();
  const selectedEnvId = useSelectedEnv();
  const { urlFilters } = useUrlFilters({ urlKey: COVERAGE_TABLE_FILTERS });
  const urlFiltersDebounced = useDebounce(urlFilters, 1000);
  const { selectedTreeFolderNodes } = useSelectedTreeItem();
  const [searchParams] = useSearchParams();
  const daysBack = parseInt(searchParams.get(DAYS_BACK) || '7', 10);

  // Update chart width when container size changes
  useEffect(() => {
    const updateChartWidth = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateChartWidth();

    // Add resize listener
    window.addEventListener('resize', updateChartWidth);

    // Also use ResizeObserver for more precise container size changes
    const resizeObserver = new ResizeObserver(updateChartWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateChartWidth);
      resizeObserver.disconnect();
    };
  }, []);

  const apiFilters = useMemo((): TableAssetsQueryRequest['filters'] => {
    const filters: TableAssetsQueryRequest['filters'] = {};

    if (selectedTreeFolderNodes?.length) {
      const nonTestNodes = selectedTreeFolderNodes.filter(
        (node) => node.resourceType !== 'test',
      );
      if (nonTestNodes.length) {
        filters.ids = nonTestNodes.map((node) => node.id);
      }
    }

    urlFiltersDebounced?.forEach((filter) => {
      switch (filter.property) {
        case 'ids':
          filters.ids = filter.value;
          break;
        case 'names':
          filters.names = filter.value;
          break;
        case 'isCritical':
          filters.isCritical = filter.value[0] === 'true';
          break;
        case 'search':
          filters.search = filter.value[0];
          break;
        case 'tags':
          filters.tags = filter.value;
          break;
        case 'owners':
          filters.owners = filter.value;
          break;
        case 'testNames':
          filters.testNames = filter.value;
          break;
        case 'lineage':
          if (filter.operator === 'upstreamFrom') {
            filters.upstreamFrom = filter.value;
          } else if (filter.operator === 'downstreamFrom') {
            filters.downstreamFrom = filter.value;
          }
          break;
        case 'coverage.percentageAmount':
          filters.percentageAmount = filter.value.map((item) => {
            const [min, max] = item
              .replaceAll('%', '')
              .split('-')
              .map((v) => Number(v));
            return { min, max };
          });
          break;
        default:
          if (filter.property.startsWith('coverage.')) {
            const dimension = filter.property.split('.')[1];
            if (!filters.dimensionTestNames) {
              filters.dimensionTestNames = {};
            }
            filters.dimensionTestNames[
              dimension as keyof typeof filters.dimensionTestNames
            ] = filter.value;
          }
          break;
      }
    });

    return filters;
  }, [urlFiltersDebounced, selectedTreeFolderNodes]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [COVERAGE_STATS, selectedEnvId, apiFilters, daysBack],
    queryFn: () => fetchCoverageStats(selectedEnvId!, apiFilters, daysBack),
    enabled: !!selectedEnvId,
  });

  const {
    totalAssets = undefined,
    totalCriticalAssets = undefined,
    avgCoverage = undefined,
    avgCriticalCoverage = undefined,
    dimensionPercentages = {},
  } = data || {};

  const chartData = useMemo(() => {
    const dimensions = [
      { name: 'freshness', label: 'Freshness' },
      { name: 'completeness', label: 'Completeness' },
      { name: 'uniqueness', label: 'Uniqueness' },
      { name: 'validity', label: 'Validity' },
      { name: 'accuracy', label: 'Accuracy' },
      { name: 'consistency', label: 'Consistency' },
      { name: 'other', label: 'Other' },
    ];

    return dimensions.map((dimension) => ({
      name: dimension.label,
      coverage: dimensionPercentages[dimension.name] || 0,
    }));
  }, [dimensionPercentages]);

  return (
    <SummaryWrapper sx={sx}>
      <StatCard>
        <CardHeader>
          <CardTitle>Assets Coverage</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingCoverage />
          ) : isError ? (
            <ErrorCoverage />
          ) : (
            <>
              <ScoreContainer>
                <ScoreValue>{avgCoverage?.toFixed(0) || 0}%</ScoreValue>
                <BarStack
                  value={Number(avgCoverage?.toFixed(0) || 0)}
                  size="large"
                />
              </ScoreContainer>
              <TotalAssets>
                <TotalAssetsValue>
                  {totalAssets?.toLocaleString()}
                </TotalAssetsValue>
                <TotalAssetsLabel>Total assets</TotalAssetsLabel>
              </TotalAssets>
            </>
          )}
        </CardContent>
      </StatCard>

      <StatCard>
        <CardHeader>
          <CardTitle>Critical Assets Coverage</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingCoverage />
          ) : isError ? (
            <ErrorCoverage />
          ) : (
            <>
              <ScoreContainer>
                <ScoreValue>{avgCriticalCoverage?.toFixed(0) || 0}%</ScoreValue>
                <BarStack
                  value={Number(avgCriticalCoverage?.toFixed(0) || 0)}
                  size="large"
                />
              </ScoreContainer>
              <TotalAssets>
                <TotalAssetsValue>
                  {totalCriticalAssets?.toLocaleString()}
                </TotalAssetsValue>
                <TotalAssetsLabel>Total critical assets</TotalAssetsLabel>
              </TotalAssets>
            </>
          )}
        </CardContent>
      </StatCard>

      <DimensionsCard>
        <CardHeader>
          <CardTitle>Coverage by Dimensions</CardTitle>
        </CardHeader>
        <DimensionsContent>
          <ChartContainer ref={chartContainerRef}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 0, left: 25, bottom: -3 }}
              >
                <YAxis type="number" domain={[0, 100]} hide />
                <XAxis
                  type="category"
                  dataKey="name"
                  tick={(props) => (
                    <CustomXAxisTick {...props} chartWidth={chartWidth || 0} />
                  )}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                />
                <Tooltip content={<CustomTooltipContent />} cursor={false} />
                <Bar
                  dataKey="coverage"
                  fill={theme.palette.common.black}
                  radius={[2, 2, 0, 0]}
                  barSize={7}
                />
                <ReferenceLine
                  y={0}
                  name="0"
                  stroke={theme.palette.common.gray18}
                  strokeWidth={0.5}
                  label={{
                    value: '0%',
                    position: 'insideLeft',
                    offset: -25,
                    fill: theme.palette.common.red2,
                    fontSize: 11,
                    fontFamily: 'DM Sans',
                    fontWeight: 'bold',
                  }}
                />
                <ReferenceLine
                  y={50}
                  stroke={theme.palette.common.gray18}
                  strokeDasharray="5"
                  strokeWidth={0.5}
                  name="50"
                  label={{
                    value: '50%',
                    position: 'insideLeft',
                    offset: -25,
                    fill: theme.palette.common.yellow,
                    fontSize: 11,
                    fontFamily: 'DM Sans',
                    fontWeight: 'bold',
                  }}
                />

                <ReferenceLine
                  y={100}
                  stroke={theme.palette.common.gray18}
                  strokeDasharray="5"
                  strokeWidth={0.5}
                  name="100"
                  label={{
                    value: '100%',
                    position: 'insideLeft',
                    offset: -25,
                    fill: theme.palette.common.green100,
                    fontSize: 11,
                    fontFamily: 'DM Sans',
                    fontWeight: 'bold',
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </DimensionsContent>
      </DimensionsCard>
    </SummaryWrapper>
  );
};

export default CoverageSummary;
